import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Compass,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Settings,
  Volume2,
  VolumeX,
} from "lucide-react";
import Hls from "hls.js";
import { Feature } from "../../config/features";
import ViewerLoading from "./ViewerLoading";

const SPEEDS = [0.5, 1, 1.5, 2];

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function WalkthroughPlayer({
  streamUrl,
  momentsUrl,
  label,
  onFullscreen,
  isFullscreen,
  stageRef,
}) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [moments, setMoments] = useState([]);
  const [activeMoment, setActiveMoment] = useState(0);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [buffering, setBuffering] = useState(false);

  useEffect(() => {
    if (!momentsUrl) return undefined;
    let cancelled = false;
    fetch(momentsUrl)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.moments) return;
        const base = momentsUrl.replace(/[^/]+$/, "");
        setMoments(
          data.moments.map((item) => ({
            ...item,
            src: item.thumb.startsWith("http") || item.thumb.startsWith("/")
              ? item.thumb
              : `${base}${item.thumb}`,
          }))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [momentsUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) return undefined;
    setFailed(false);
    setReady(false);

    if (Hls.isSupported()) {
      const hls = new Hls({
        capLevelToPlayerSize: true,
        startLevel: -1,
      });
      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal) setFailed(true);
      });
      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = streamUrl;
      return () => {
        video.removeAttribute("src");
        video.load();
      };
    }

    setFailed(true);
    return undefined;
  }, [streamUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onTime = () => {
      setCurrent(video.currentTime);
      if (moments.length) {
        let idx = 0;
        moments.forEach((item, i) => {
          if (video.currentTime >= item.time - 0.25) idx = i;
        });
        setActiveMoment(idx);
      }
    };
    const onMeta = () => {
      setDuration(video.duration || 0);
      setReady(true);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => {
      setPlaying(false);
      setBuffering(false);
    };
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplay", onPlaying);
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", onPlaying);
    };
  }, [moments]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }, []);

  const seekTo = (time) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = time;
    setCurrent(time);
  };

  const onScrub = (event) => {
    if (!duration) return;
    seekTo((Number(event.target.value) / 1000) * duration);
  };

  const changeSpeed = (next) => {
    const video = videoRef.current;
    setSpeed(next);
    setSpeedOpen(false);
    if (video) video.playbackRate = next;
  };

  const progress = duration > 0 ? (current / duration) * 1000 : 0;

  if (failed) {
    return (
      <div className="sv-placeholder">
        <Play size={36} />
        <h3>{label}</h3>
        <p>{t("siteVisuals.walkthroughFailed")}</p>
      </div>
    );
  }

  return (
    <div className="sv-walkthrough">
      <div className="sv-walk-stage" ref={stageRef}>
        <video
          ref={videoRef}
          className="sv-walkthrough-video"
          playsInline
          preload="metadata"
          onClick={togglePlay}
        />
        {!ready && !failed && <ViewerLoading label={t("siteVisuals.loadingWalkthrough")} />}
        {ready && buffering && <ViewerLoading label={t("siteVisuals.buffering")} subtle />}
        <span className="sv-walk-badge">{label}</span>
        <Feature name="WALKTHROUGH_COMPASS">
          <button type="button" className="sv-walk-compass" title="North" tabIndex={-1}>
            <Compass size={16} />
          </button>
        </Feature>
        <div className="sv-walk-controls">
          <button type="button" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"}>
            {playing ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button
            type="button"
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              video.muted = !video.muted;
              setMuted(video.muted);
            }}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="sv-walk-time">
            {formatTime(current)} / {formatTime(duration)}
          </span>
          <input
            type="range"
            min="0"
            max="1000"
            value={progress}
            onChange={onScrub}
            className="sv-walk-scrub"
            aria-label="Seek"
          />
          <div className="sv-walk-speed">
            <button type="button" onClick={() => setSpeedOpen((open) => !open)}>
              {speed.toFixed(1)}x
            </button>
            {speedOpen && (
              <div className="sv-walk-speed-menu">
                {SPEEDS.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={item === speed ? "active" : ""}
                    onClick={() => changeSpeed(item)}
                  >
                    {item.toFixed(1)}x
                  </button>
                ))}
              </div>
            )}
          </div>
          <Feature name="WALKTHROUGH_SETTINGS">
            <button type="button" title="Settings" aria-label="Settings">
              <Settings size={15} />
            </button>
          </Feature>
          <button
            type="button"
            onClick={onFullscreen}
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
        </div>
      </div>

      {moments.length > 0 && (
        <div className="sv-key-moments">
          <h3>{t("siteVisuals.keyMoments")}</h3>
          <div className="sv-key-moments-row">
            {moments.map((item, index) => (
              <button
                key={item.time}
                type="button"
                className={`sv-key-moment ${index === activeMoment ? "active" : ""}`}
                onClick={() => {
                  seekTo(item.time);
                  videoRef.current?.play().catch(() => {});
                }}
              >
                <img src={item.src} alt="" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WalkthroughPlayer;
